import React, { useState } from "react";
import { Building2, ArrowLeftRight } from "lucide-react";
import TopCompanies from "./TopCompanies.jsx";
import Sparkline from "./Sparkline.jsx";
import { CATEGORIES } from "../data/companies.js";
import { getStockStats } from "../utils/priceData.js";

// The column cycles through these, one at a time, when the header button is pressed
const METRICS = [
  { id: "change", label: "52W Change" },
  { id: "current", label: "Current Price" },
  { id: "high", label: "52W High" },
  { id: "low", label: "52W Low" },
];

const money = (n) => `$${n.toFixed(2)}`;

function MetricValue({ metric, stats }) {
  if (metric === "change") {
    const up = stats.change >= 0;
    return (
      <span className={`fw-bold ${up ? "change-up" : "change-down"}`}>
        {up ? "+" : ""}
        {stats.change.toFixed(1)}%
      </span>
    );
  }
  return <span className="fw-bold">{money(stats[metric])}</span>;
}

// "All" view: one standout company from three different categories
const ALL_FEATURED = [
  CATEGORIES[1].companies[0], // NVIDIA
  CATEGORIES[2].companies[0], // JPMorgan
  CATEGORIES[3].companies[0], // Eli Lilly
];

export default function Companies({ user }) {
  const [categoryId, setCategoryId] = useState("all");
  const [metricIndex, setMetricIndex] = useState(0);
  const metric = METRICS[metricIndex];
  const nextMetric = () => setMetricIndex((metricIndex + 1) % METRICS.length);

  const selected = CATEGORIES.find((c) => c.id === categoryId);

  // Companies listed in the table, tagged with their category
  const tableRows = (selected ? [selected] : CATEGORIES).flatMap((cat) =>
    cat.companies.map((c) => ({ ...c, category: cat.label }))
  );

  return (
    <main className="page-bg">
      <div className="dash-main-content">
        <div className="top-bar">
          <div className="watchlist-title">
            <Building2 size={18} />
            <span>Companies</span>
          </div>
        </div>

        <p className="text-muted mb-3">
          {user
            ? "Browse companies by category. Soon you'll see suggestions based on your interests."
            : "Browse companies by category. Sign up to follow them and personalize your experience."}
        </p>

        {/* Category pills */}
        <div className="category-pills mb-4">
          <button
            className={`category-pill ${categoryId === "all" ? "active" : ""}`}
            onClick={() => setCategoryId("all")}
          >
            All
          </button>
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              className={`category-pill ${categoryId === cat.id ? "active" : ""}`}
              onClick={() => setCategoryId(cat.id)}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* 3 featured companies with charts */}
        <TopCompanies
          title={selected ? `Featured in ${selected.label}` : "Featured companies"}
          companies={selected ? selected.companies : ALL_FEATURED}
        />

        {/* Companies in the chosen category */}
        <h5 className="fw-bold mb-3">
          {selected ? `${selected.label} companies` : "All companies"}
        </h5>
        <div className="card">
          <div className="table-responsive">
            <table className="table align-middle mb-0 companies-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Ticker</th>
                  <th>Category</th>
                  <th className="text-end">
                    <button
                      className="metric-toggle"
                      onClick={nextMetric}
                      title="Click to switch: 52W change, current price, 52W high, 52W low"
                    >
                      {metric.label} <ArrowLeftRight size={13} />
                    </button>
                  </th>
                  <th className="text-end">52W Chart</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((c) => (
                  <tr key={c.symbol}>
                    <td className="fw-bold"><a className="wl-company" href={`#/stock/${c.ticker}`}>{c.name}</a></td>
                    <td className="text-muted">{c.ticker}</td>
                    <td>
                      <span className="badge badge-purple">{c.category}</span>
                    </td>
                    <td className="text-end">
                      <MetricValue metric={metric.id} stats={getStockStats(c.ticker)} />
                    </td>
                    <td className="text-end">
                      <Sparkline data={getStockStats(c.ticker).series} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-muted small mt-3 mb-0">
          Prices and charts in this table are sample data for layout purposes.
        </p>
      </div>
    </main>
  );
}
